import { Component, Input, OnInit } from '@angular/core';
import { DashboardModel, DashboardModelDefaultValues } from '../dashboard-model';
import { DashboardAnalyticsModel } from '../dashboard-analytics-model';
import { DashboardService } from 'src/app/services/dashboard.service';
import { AuthService } from 'src/app/services/auth.service';
import { AccessControl, AccessControlDefaultValues } from '../../users/access-control';

interface StatMetric {
  key: string;
  label: string;
  icon: string;
  accent: 'teal' | 'blue' | 'purple' | 'amber' | 'green';
  getValue: (analytics: DashboardAnalyticsModel) => number;
}

interface InvitationMetric {
  label: string;
  tone: 'default' | 'warning' | 'danger';
  getValue: () => number;
}

interface QuickLink {
  label: string;
  description: string;
  icon: string;
  route: string;
  visible: boolean;
}

@Component({
    selector: 'app-dashboard-bbprogramadministrator',
    templateUrl: './dashboard-bbprogramadministrator.component.html',
    styleUrls: ['./dashboard-bbprogramadministrator.component.scss'],
    standalone: false
})
export class DashboardBbprogramadministratorComponent implements OnInit {
  @Input() dashboardModel: DashboardModel = DashboardModelDefaultValues();

  accessControl: AccessControl = AccessControlDefaultValues();
  startDate: Date = new Date();
  endDate: Date = new Date();
  coachedParticipantCount = 0;
  drpCoachParticipantValue = '1';

  readonly periodOptions = [
    { value: '1', label: 'Last 30 days' },
    { value: '2', label: 'Last 3 months' },
    { value: '3', label: 'Last year' }
  ];

  readonly platformMetrics: StatMetric[] = [
    { key: 'activeAdminUsers', label: 'BB Admin Users', icon: 'admin_panel_settings', accent: 'blue', getValue: (a) => a.activeAdminUsers },
    { key: 'bbCoachedParticipants', label: 'BB Coached', icon: 'support_agent', accent: 'green', getValue: (a) => a.bbCoachedParticipants },
    { key: 'bbSelfGuidedParticipants', label: 'BB Self-Guided', icon: 'menu_book', accent: 'teal', getValue: (a) => a.bbSelfGuidedParticipants },
    { key: 'courses', label: 'Courses', icon: 'library_books', accent: 'amber', getValue: (a) => a.courses },
    { key: 'materials', label: 'Materials', icon: 'description', accent: 'amber', getValue: (a) => a.materials }
  ];

  private readonly metricsByRole: Record<string, string[]> = {
    'BB Superadmin': ['activeAdminUsers', 'bbCoachedParticipants', 'bbSelfGuidedParticipants', 'courses', 'materials'],
    'BounceBack Division Program Lead': ['activeAdminUsers', 'bbCoachedParticipants', 'bbSelfGuidedParticipants', 'courses', 'materials'],
    'BounceBack Provincial Support': [],
    'BounceBack Data Analysis': ['bbCoachedParticipants', 'bbSelfGuidedParticipants'],
    'BounceBack Data Analysis BC': ['bbCoachedParticipants', 'bbSelfGuidedParticipants'],
    'BounceBack Material Management': ['courses', 'materials'],
    'BounceBack Branch Admin': [],
    'BounceBack Coach': []
  };

  bbInvitationMetrics: InvitationMetric[] = [];
  quickLinks: QuickLink[] = [];

  constructor(
    private _dashboardService: DashboardService,
    private _authService: AuthService
  ) {}

  ngOnInit(): void {
    this.accessControl = this._authService.getAccessControl();
    this.buildInvitationMetrics();
    this.buildQuickLinks();
    if (this.dashboardModel.showParticipantGrowthSection) {
      this.getCoachedParticipantCount();
    }
  }

  get visibleQuickLinks(): QuickLink[] {
    return this.quickLinks.filter((link) => link.visible);
  }

  get visiblePlatformMetrics(): StatMetric[] {
    if (!this.dashboardModel.showMetricsSection) {
      return [];
    }

    const allowedKeys = this.metricsByRole[this.dashboardModel.dashboardRoleLabel] ?? [];
    return this.platformMetrics.filter((metric) => allowedKeys.includes(metric.key));
  }

  get totalBbParticipants(): number {
    const analytics = this.dashboardModel.analytics;
    return analytics.bbCoachedParticipants + analytics.bbSelfGuidedParticipants;
  }

  get showHeroStat(): boolean {
    return this.totalBbParticipants > 0;
  }

  get showGrowthEmptyHint(): boolean {
    return this.coachedParticipantCount === 0;
  }

  setPeriod(value: string): void {
    if (this.drpCoachParticipantValue === value) {
      return;
    }
    this.drpCoachParticipantValue = value;
    this.ondropdownchange();
  }

  private buildInvitationMetrics(): void {
    this.bbInvitationMetrics = [
      {
        label: 'Not accepted',
        tone: 'default',
        getValue: () => this.dashboardModel.invitationsNotAcceptedCount
      },
      {
        label: 'Expiring soon',
        tone: 'warning',
        getValue: () => this.dashboardModel.invitationsExpiringCount
      },
      {
        label: 'Expired',
        tone: 'danger',
        getValue: () => this.dashboardModel.invitationsExpiredCount
      }
    ];
  }

  private buildQuickLinks(): void {
    this.quickLinks = [
      {
        label: 'User Management',
        description: 'View and manage BounceBack admin users',
        icon: 'group',
        route: '/user-list',
        visible: this.accessControl.userManagement
      },
      {
        label: 'BB Invitations',
        description: 'Create and manage coach invitations',
        icon: 'outgoing_mail',
        route: '/bb-invite-list',
        visible: this.accessControl.bbInvitaitons
      },
      {
        label: 'Courses',
        description: 'Manage course content',
        icon: 'library_books',
        route: '/course-list',
        visible: this.accessControl.courses
      },
      {
        label: 'Materials',
        description: 'Browse and edit course materials',
        icon: 'description',
        route: '/material-list',
        visible: this.accessControl.materials
      },
      {
        label: 'Tags',
        description: 'Organize content with tags',
        icon: 'label',
        route: '/tags-list',
        visible: this.accessControl.tags
      },
      {
        label: 'Email Queue',
        description: 'Review pending outbound emails',
        icon: 'mail',
        route: '/email-queue',
        visible: this.accessControl.emailQueue
      }
    ];
  }

  getCoachedParticipantCount(): void {
    this.startDate.setDate(this.startDate.getDate() - 30);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.endDate = tomorrow;

    this._dashboardService
      .getCoachedParticipantCountForCoachAndAdmin(this.startDate, this.endDate)
      .subscribe({
        next: (data) => {
          this.coachedParticipantCount = data;
        }
      });
  }

  ondropdownchange(): void {
    const startDate = new Date();
    switch (this.drpCoachParticipantValue) {
      case '1':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '2':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case '3':
        startDate.setDate(startDate.getDate() - 365);
        break;
      default:
        break;
    }
    this.startDate = startDate;
    this.getCoachedParticipantCount();
  }
}
